export type AssistantModalTheme = 'light' | 'dark';

interface AssistantModalTokens {
  paperBg: string;
  headerBg: string;
  bodyBg: string;
  surface: string;
  surface2: string;
  border: string;
  borderMid: string;
  borderHeavy: string;
  textPrimary: string;
  textSecondary: string;
  textDisabled: string;
  inputBg: string;
  cancelBorder: string;
  cancelHoverBg: string;
  cancelHoverBorder: string;
  backdrop: string;
  accentSurface: string;
  accentText: string;
  labelColor: string;
  closeColor: string;
  toggleBg: string;
  toggleBorder: string;
  toggleColor: string;
  toggleActiveBg: string;
  toggleActiveBorder: string;
  toggleActiveColor: string;
  toggleFontSize: string;
  togglePadding: string;
  toggleActiveWeight: string;
  tabActiveBorder: string;
  tabActiveBg: string;
  tabActiveColor: string;
  inlineBtnBorder: string;
  inlineBtnBg: string;
  inlineBtnColor: string;
  inlineBtnHoverBg: string;
  iconAccent: string;
  focusBorderColor: string;
  successColor: string;
  successBg: string;
  // Language-tab "translated" chip styling. Kept separate from successColor
  // because in dark mode the chip itself should read as normal text/border —
  // only the checkmark badge inside it (LangCompleteBadge, via successColor)
  // stays green — while light mode keeps the original green chip look.
  langCompleteBorderColor: string;
  langCompleteTextColor: string;
  langCompleteBg: string;
  langTabActiveWeight: string;
  warningColor: string;
  warningBg: string;
  primaryBtnBg: string;
  primaryBtnHoverBg: string;
  primaryBtnColor: string;
  errorColor: string;
  errorBg: string;
  previewPanelBg: string;
  accordionContentBg: string;
  menuAccentColor: string;
  ghostBtnColor: string;
  ghostBtnBorder: string;
  ghostBtnHoverBg: string;
}

const lightTokens: AssistantModalTokens = {
  paperBg: '#ffffff',
  headerBg: '#ffffff',
  bodyBg: '#ffffff',
  surface: '#fafafa',
  surface2: '#f5f5f5',
  border: '#eeeeee',
  borderMid: '#e0e0e0',
  borderHeavy: '#bdbdbd',
  textPrimary: 'rgba(0,0,0,0.87)',
  textSecondary: 'rgba(0,0,0,0.6)',
  textDisabled: 'rgba(0,0,0,0.38)',
  inputBg: 'transparent',
  cancelBorder: '#e0e0e0',
  cancelHoverBg: '#fafafa',
  cancelHoverBorder: '#bdbdbd',
  backdrop: 'rgba(0,0,0,0.5)',
  accentSurface: 'rgba(69,84,229,0.07)',
  accentText: '#1565c0',
  labelColor: 'rgba(0,0,0,0.5)',
  closeColor: 'rgba(0,0,0,0.54)',
  toggleBg: 'transparent',
  toggleBorder: '2px solid #e0e0e0',
  toggleColor: 'rgba(0,0,0,0.6)',
  toggleActiveBg: 'rgba(25,118,210,0.08)',
  toggleActiveBorder: '2px solid #1976d2',
  toggleActiveColor: '#1976d2',
  toggleFontSize: '13px',
  togglePadding: '6px 16px',
  toggleActiveWeight: '600',
  tabActiveBorder: '1.5px solid #1976d2',
  tabActiveBg: 'rgba(25,118,210,0.08)',
  tabActiveColor: '#1976d2',
  inlineBtnBorder: '1.5px solid rgba(25,118,210,0.4)',
  inlineBtnBg: 'rgba(25,118,210,0.05)',
  inlineBtnColor: '#1976d2',
  inlineBtnHoverBg: 'rgba(25,118,210,0.09)',
  iconAccent: '#1976d2',
  focusBorderColor: '#1976d2',
  successColor: '#2e7d32',
  successBg: 'rgba(46,125,50,0.08)',
  langCompleteBorderColor: '#2e7d32',
  langCompleteTextColor: '#2e7d32',
  langCompleteBg: 'rgba(46,125,50,0.08)',
  langTabActiveWeight: '600',
  warningColor: '#ed6c02',
  warningBg: 'rgba(237,108,2,0.08)',
  primaryBtnBg: '#1976d2',
  primaryBtnHoverBg: '#1565c0',
  primaryBtnColor: 'rgb(255, 255, 255)',
  errorColor: '#d32f2f',
  errorBg: 'rgba(211,47,47,0.1)',
  previewPanelBg: 'rgb(61, 61, 61)',
  accordionContentBg: 'transparent',
  menuAccentColor: 'rgba(34, 51, 84, 0.8)',
  ghostBtnColor: '#1976d2',
  ghostBtnBorder: '1.5px solid rgba(25,118,210,0.4)',
  ghostBtnHoverBg: 'rgba(25,118,210,0.09)',
};

// Dark token values were originally taken 1:1 from the noa_script_editor.jsx
// reference palette; the accent/success/warning/error hues have since been
// realigned to match apps/hub's actual EditorMode panels (see
// AiScripts/inspectorStyles.ts and Inspector/styles.ts), so the assistant
// modals and the in-world Editor Mode share one dark accent language:
// bg:#0f1117, surface:#181a24, surface2:#1f2233, border:#2a2d3e, borderLight:#353849,
// text:#e2e4ed, textMuted:#8b8fa3, textDim:#5c6078, accent:rgb(110,108,246),
// green:#3fcf6e, amber:#f0b14c, red:#f06d6d
const darkTokens: AssistantModalTokens = {
  paperBg: '#0f1117',
  headerBg: '#181a24',
  bodyBg: '#0f1117',
  surface: '#181a24',
  surface2: '#1f2233',
  border: '#2a2d3e',
  borderMid: '#353849',
  borderHeavy: '#6e6cf6',
  textPrimary: '#e2e4ed',
  textSecondary: '#8b8fa3',
  textDisabled: '#5c6078',
  inputBg: '#1f2233',
  cancelBorder: '#2a2d3e',
  cancelHoverBg: 'rgba(255,255,255,0.04)',
  cancelHoverBorder: '#353849',
  backdrop: 'rgba(0,0,0,0.45)',
  accentSurface: 'rgba(110,108,246,0.125)',
  accentText: '#6e6cf6',
  labelColor: '#8b8fa3',
  closeColor: '#8b8fa3',
  toggleBg: '#1f2233',
  toggleBorder: '1.5px solid #2a2d3e',
  toggleColor: '#8b8fa3',
  toggleActiveBg: 'rgba(110,108,246,0.125)',
  toggleActiveBorder: '1.5px solid #6e6cf6',
  toggleActiveColor: '#6e6cf6',
  toggleFontSize: '12px',
  togglePadding: '6px 12px',
  toggleActiveWeight: '700',
  tabActiveBorder: '1.5px solid #6e6cf6',
  tabActiveBg: 'rgba(110,108,246,0.125)',
  tabActiveColor: '#6e6cf6',
  inlineBtnBorder: '1px solid rgba(110,108,246,0.314)',
  inlineBtnBg: 'rgba(110,108,246,0.125)',
  inlineBtnColor: '#6e6cf6',
  inlineBtnHoverBg: 'rgba(110,108,246,0.18)',
  iconAccent: '#6e6cf6',
  focusBorderColor: '#6e6cf6',
  successColor: '#10b981',
  successBg: 'rgba(16,185,129,0.12)',
  langCompleteBorderColor: '#353849',
  langCompleteTextColor: '#e2e4ed',
  langCompleteBg: 'transparent',
  langTabActiveWeight: '400',
  warningColor: '#f0b14c',
  warningBg: 'rgba(240,177,76,0.12)',
  primaryBtnBg: '#6e6cf6',
  primaryBtnHoverBg: '#5654d1',
  primaryBtnColor: 'rgb(226, 228, 237)',
  errorColor: '#f06d6d',
  errorBg: 'rgba(240,109,109,0.13)',
  previewPanelBg: '#181a24',
  accordionContentBg: 'var(--am-surface)',
  menuAccentColor: '#6e6cf6',
  ghostBtnColor: 'rgb(226, 228, 237)',
  ghostBtnBorder: '1px solid #2a2d3e',
  ghostBtnHoverBg: 'rgba(255,255,255,0.04)',
};

export const modalThemeTokens: Record<
  AssistantModalTheme,
  AssistantModalTokens
> = {
  light: lightTokens,
  dark: darkTokens,
};

export function buildModalCssVars(
  tokens: AssistantModalTokens,
): Record<string, string> {
  return {
    '--am-paper-bg': tokens.paperBg,
    '--am-header-bg': tokens.headerBg,
    '--am-body-bg': tokens.bodyBg,
    '--am-surface': tokens.surface,
    '--am-surface-2': tokens.surface2,
    '--am-border': tokens.border,
    '--am-border-mid': tokens.borderMid,
    '--am-border-heavy': tokens.borderHeavy,
    '--am-text-primary': tokens.textPrimary,
    '--am-text-secondary': tokens.textSecondary,
    '--am-text-disabled': tokens.textDisabled,
    '--am-input-bg': tokens.inputBg,
    '--am-cancel-border': tokens.cancelBorder,
    '--am-cancel-hover-bg': tokens.cancelHoverBg,
    '--am-cancel-hover-border': tokens.cancelHoverBorder,
    '--am-backdrop': tokens.backdrop,
    '--am-accent-surface': tokens.accentSurface,
    '--am-accent-text': tokens.accentText,
    '--am-label-color': tokens.labelColor,
    '--am-close-color': tokens.closeColor,
    '--am-toggle-bg': tokens.toggleBg,
    '--am-toggle-border': tokens.toggleBorder,
    '--am-toggle-color': tokens.toggleColor,
    '--am-toggle-active-bg': tokens.toggleActiveBg,
    '--am-toggle-active-border': tokens.toggleActiveBorder,
    '--am-toggle-active-color': tokens.toggleActiveColor,
    '--am-toggle-font-size': tokens.toggleFontSize,
    '--am-toggle-padding': tokens.togglePadding,
    '--am-toggle-active-weight': tokens.toggleActiveWeight,
    '--am-tab-active-border': tokens.tabActiveBorder,
    '--am-tab-active-bg': tokens.tabActiveBg,
    '--am-tab-active-color': tokens.tabActiveColor,
    '--am-inline-btn-border': tokens.inlineBtnBorder,
    '--am-inline-btn-bg': tokens.inlineBtnBg,
    '--am-inline-btn-color': tokens.inlineBtnColor,
    '--am-inline-btn-hover-bg': tokens.inlineBtnHoverBg,
    '--am-icon-accent': tokens.iconAccent,
    '--am-focus-border-color': tokens.focusBorderColor,
    '--am-success-color': tokens.successColor,
    '--am-success-bg': tokens.successBg,
    '--am-lang-complete-border-color': tokens.langCompleteBorderColor,
    '--am-lang-complete-text-color': tokens.langCompleteTextColor,
    '--am-lang-complete-bg': tokens.langCompleteBg,
    '--am-lang-tab-active-weight': tokens.langTabActiveWeight,
    '--am-warning-color': tokens.warningColor,
    '--am-warning-bg': tokens.warningBg,
    '--am-primary-btn-bg': tokens.primaryBtnBg,
    '--am-primary-btn-hover-bg': tokens.primaryBtnHoverBg,
    '--am-primary-btn-color': tokens.primaryBtnColor,
    '--am-error-color': tokens.errorColor,
    '--am-error-bg': tokens.errorBg,
    '--am-preview-panel-bg': tokens.previewPanelBg,
    '--am-accordion-content-bg': tokens.accordionContentBg,
    '--am-menu-accent-color': tokens.menuAccentColor,
    '--am-ghost-btn-color': tokens.ghostBtnColor,
    '--am-ghost-btn-border': tokens.ghostBtnBorder,
    '--am-ghost-btn-hover-bg': tokens.ghostBtnHoverBg,
  };
}
